import { formatMoney, newLineKey, parseNonNegative, type LineItem } from '@/lib/line-items';
import type { ItemSuggestion } from '@/styles/GenericItemAutocomplete';
import type { CatalogSearchOffer, CatalogSearchRow } from '../../catalog/services/catalog-api';

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * How a purchase order's lines get their prices and vendors, how a line stays linked to (or leaves) its catalog item, and how lines are
 * saved. Pure functions: the form, the "Add from Catalog" popup and the tests all use these, so the rules exist once.
 *
 *  PRICE AND VENDOR OF A NEW LINE
 *  1. The order has a vendor and the item has an offer from it: that vendor and its price.
 *  2. The order has no vendor: the item's PREFERRED vendor.
 *  3. No preferred vendor: the first vendor of the item's list -- which is its only vendor when there is one. "The list" is the one every picker of the
 *     app shows (see `sortedOffers`): the preferred first, then the cheapest, ties by offer id.
 *  A vendor that is not one of the item's vendors is NEVER chosen. If the order's vendor has no offer for the item the line keeps price 0 and no vendor
 *  (never a made-up price, never another vendor's: the order is addressed to that vendor).
 *  - Each line remembers WHICH vendor its price comes from (`vendorId`) and can be switched to any other vendor that has an offer for the item.
 *
 *  LINK TO THE CATALOG
 *  - A line picked from the catalog is linked to its item and remembers what the item said (`linkBaseline`: name, cost code, price).
 *  - Changing the line's NAME, COST CODE or UNIT PRICE makes it a custom line: it is unlinked (catalog_item_id = null, saved that way) and
 *    keeps its own values. Changing only the QUANTITY never unlinks: how many are wanted says nothing about what the item is or costs.
 *  - Nothing here ever writes to the catalog item.
 *
 *  COST CODE
 *  - Every line carries a cost code BY ID. A catalog item's own code is preselected; a blank or custom line starts with the company's default
 *    code (the caller resolves it from the company's real records). There is no "none" choice.
 *
 *  VENDOR OF A LINE
 *  - A vendor the user chose for a line by hand (`vendorChoice` 'manual') is kept when the order's vendor changes, as long as that vendor still has an
 *    offer for the item; only the lines whose vendor came from the rules above are priced again.
 *  - A linked line chooses among the vendors that really have an offer for its item (the offer fixes the price). A custom line may name any
 *    of the company's vendors: that is just a label with a hand-typed price, never an offer. The vendor of a line is never the order's vendor.
 *  - The star of a price: the line's price IS the current price of the preferred vendor's offer (see `usesPreferredOffer`).
 */

export type PriceResolution =
  | { status: 'priced'; price: number; vendorId: string; from: 'vendor' | 'preferred' | 'listed' }
  | { status: 'no-offer' }; // the order's vendor has no (active) offer for this item, or the item has no active offer at all

export function resolvePrice(offers: CatalogSearchOffer[], vendorId: string | null | undefined): PriceResolution {
  const active = offers.filter((offer) => offer.is_active);
  if (vendorId) {
    const offer = active.find((o) => o.vendor_id === vendorId);
    return offer ? { status: 'priced', price: Number(offer.unit_price), vendorId: offer.vendor_id, from: 'vendor' } : { status: 'no-offer' };
  }
  const first = sortedOffers(active)[0]; // the preferred one comes first; with none, the first of the list
  return first ? { status: 'priced', price: Number(first.unit_price), vendorId: first.vendor_id, from: first.is_preferred ? 'preferred' : 'listed' } : { status: 'no-offer' };
}

/** A price as text for the box: 2 decimals, more only when the price really has them ("12.5" -> "12.50", "0.0625" stays). */
export const priceText = (price: number): string => {
  const rounded = Math.round(price * 10000) / 10000;
  return Math.abs(rounded * 100 - Math.round(rounded * 100)) < 1e-9 ? rounded.toFixed(2) : String(rounded);
};

export const costCodeText = (costCode: CatalogSearchRow['cost_code']): string => (costCode ? (costCode.name ? `${costCode.code} · ${costCode.name}` : costCode.code) : '');

const baselineOf = (line: Pick<LineItem, 'name' | 'costCodeId' | 'unitPrice'>): NonNullable<LineItem['linkBaseline']> => ({
  name: line.name,
  costCodeId: line.costCodeId,
  unitPrice: parseNonNegative(line.unitPrice) ?? 0,
});

/** A new line for a catalog item, priced by the rules above (quantity 1) and linked to it. Its cost code is the item's, else `defaultCostCodeId`. */
export function lineFromCatalog(item: CatalogSearchRow, vendorId: string | null | undefined, quantity = '1', defaultCostCodeId = ''): LineItem {
  const resolution = resolvePrice(item.offers, vendorId);
  const line: LineItem = {
    key: newLineKey(),
    catalogItemId: item.id,
    name: item.name,
    costCodeId: item.cost_code?.id ?? defaultCostCodeId,
    description: item.description ?? '',
    unit: item.unit ?? '',
    quantity,
    unitPrice: resolution.status === 'priced' ? priceText(resolution.price) : '0',
    priceSource: resolution.status === 'priced' ? 'catalog' : null,
    vendorId: resolution.status === 'priced' ? resolution.vendorId : null,
    linkBaseline: null,
    unlinkedNotice: false,
    billAmount: '',
    sourceLineId: null,
  };
  return { ...line, linkBaseline: baselineOf(line) };
}

/** What the price column of a catalog list says about an item for this vendor context. */
export function priceLabel(item: CatalogSearchRow, vendorId: string | null | undefined): string {
  const resolution = resolvePrice(item.offers, vendorId);
  if (resolution.status === 'priced') return formatMoney(resolution.price);
  return vendorId ? 'No price for this vendor' : 'No vendor price';
}

/** The autocomplete's option for a catalog item: enough to tell similar names apart (cost code, unit, price in this vendor context). */
export function suggestionOf(item: CatalogSearchRow, vendorId: string | null | undefined): ItemSuggestion<CatalogSearchRow> {
  return {
    id: item.id,
    name: item.name,
    costCode: costCodeText(item.cost_code),
    description: item.description ?? '',
    unit: item.unit ?? '',
    detail: [costCodeText(item.cost_code), item.sku, item.unit, priceLabel(item, vendorId)].filter(Boolean).join(' · '),
    data: item,
  };
}

/**
 * A linked line whose name, cost code or unit price no longer equals what the catalog item said becomes a custom line (unlinked). The
 * quantity is not looked at. If the PRICE moved the vendor is dropped too (the price is no longer anyone's offer). Idempotent.
 */
export function unlinkIfModified(line: LineItem): LineItem {
  if (!line.catalogItemId || !line.linkBaseline) return line;
  const base = line.linkBaseline;
  const priceChanged = (parseNonNegative(line.unitPrice) ?? 0) !== base.unitPrice;
  if (line.name === base.name && line.costCodeId === base.costCodeId && !priceChanged) return line;
  // Its price is no longer anyone's offer (the line is custom now): a hand-kept price. The vendor stays unless the price itself moved, and from here on it is
  // just a vendor named on the line.
  return { ...line, catalogItemId: null, linkBaseline: null, unlinkedNotice: true, priceSource: line.priceSource === 'catalog' ? 'manual' : line.priceSource, vendorId: priceChanged ? null : line.vendorId };
}

/** An item's offers as the lists show them: the preferred one first, then the cheapest, ties by id -- a stable order that never decides a line's vendor. */
export function sortedOffers(offers: CatalogSearchOffer[]): CatalogSearchOffer[] {
  return offers
    .filter((offer) => offer.is_active)
    .sort((a, b) => Number(b.is_preferred) - Number(a.is_preferred) || Number(a.unit_price) - Number(b.unit_price) || a.id.localeCompare(b.id));
}

/** The (up to) three prices the "Add from Catalog" list compares: real offers only, in the stable order above. The rest are not hidden anywhere else. */
export const comparisonOffers = (item: CatalogSearchRow, max = 3): CatalogSearchOffer[] => sortedOffers(item.offers).slice(0, max);

/**
 * Does this line's price come from the PREFERRED vendor's offer? Only when the line is linked to its item, its price is a catalog price, the
 * vendor it names has an active offer that is the item's preferred one, and the line's price is still that offer's price. A hand-typed price,
 * another vendor, an unlinked line or an offer that is not (or no longer) the preferred one: no star.
 */
export function usesPreferredOffer(line: LineItem, offersByItem: Map<string, CatalogSearchOffer[]>): boolean {
  if (!line.catalogItemId || !line.vendorId || line.priceSource !== 'catalog') return false;
  const offer = (offersByItem.get(line.catalogItemId) ?? []).find((o) => o.is_active && o.vendor_id === line.vendorId);
  return !!offer && offer.is_preferred && parseNonNegative(line.unitPrice) === Number(offer.unit_price);
}

/** A custom line names a vendor of the company (a label with a typed price, never an offer). Clearing it (null) is allowed. */
export const withManualVendor = (line: LineItem, vendorId: string | null): LineItem => ({ ...line, vendorId });

/** The vendors a line can switch to: the vendors with an ACTIVE offer for its catalog item (none for a custom line). */
export function lineVendorOptions(line: LineItem, offersByItem: Map<string, CatalogSearchOffer[]>): CatalogSearchOffer[] {
  if (!line.catalogItemId) return [];
  return sortedOffers(offersByItem.get(line.catalogItemId) ?? []);
}

export interface VendorSwitch {
  line: LineItem;
  /** the user typed this line's price: switching replaces it, so they must agree first */
  replacesManualPrice: boolean;
}

/** The line switched to another vendor's offer: that offer's price, that vendor, linked and unmodified. */
export function switchLineVendor(line: LineItem, offers: CatalogSearchOffer[], vendorId: string): VendorSwitch | null {
  const offer = offers.find((o) => o.is_active && o.vendor_id === vendorId);
  if (!offer) return null;
  const price = priceText(Number(offer.unit_price));
  const replacesManualPrice = line.priceSource === 'manual' && (parseNonNegative(line.unitPrice) ?? 0) !== 0 && parseNonNegative(price) !== parseNonNegative(line.unitPrice);
  const next = { ...line, vendorId: offer.vendor_id, vendorChoice: 'manual' as const, unitPrice: price, priceSource: 'catalog' as const };
  return { line: line.catalogItemId ? { ...next, linkBaseline: baselineOf(next) } : next, replacesManualPrice };
}

export interface RepriceResult {
  lines: LineItem[];
  /** lines whose price the user typed by hand and that the new vendor changes (the user must agree first) */
  manualAffected: LineItem[];
}

/**
 * The order's vendor changed: every line still linked to a catalog item is priced again with the NEW vendor's offer. A line the new vendor
 * has no offer for keeps its place, name, unit and quantity and goes back to price 0 with no vendor (never keeping the old vendor's price as
 * if it were the new one's). Custom lines are untouched. Nothing is removed and no quantity changes.
 */
export function repriceForVendor(lines: LineItem[], offersByItem: Map<string, CatalogSearchOffer[]>, vendorId: string | null | undefined): RepriceResult {
  const manualAffected: LineItem[] = [];
  const next = lines.map((line) => {
    if (!line.catalogItemId) return line;
    // a vendor the user picked for THIS line stays, while that vendor still has an active offer for the item
    if (line.vendorChoice === 'manual' && line.vendorId && (offersByItem.get(line.catalogItemId) ?? []).some((o) => o.is_active && o.vendor_id === line.vendorId)) return line;
    const resolution = resolvePrice(offersByItem.get(line.catalogItemId) ?? [], vendorId);
    const newText = resolution.status === 'priced' ? priceText(resolution.price) : '0';
    const newVendor = resolution.status === 'priced' ? resolution.vendorId : null;
    const current = parseNonNegative(line.unitPrice) ?? 0;
    const unchanged = parseNonNegative(newText) === current && newVendor === line.vendorId;
    if (line.priceSource === 'manual' && current !== 0 && parseNonNegative(newText) !== current) manualAffected.push(line);
    if (unchanged) return line;
    const repriced: LineItem = { ...line, unitPrice: newText, priceSource: resolution.status === 'priced' ? 'catalog' : null, vendorId: newVendor };
    return { ...repriced, linkBaseline: baselineOf(repriced) };
  });
  return { lines: next, manualAffected };
}

/** Why the lines cannot be saved (a bad quantity or price; a blank name is fine), or null. Only checked when saving. */
export function linesProblem(lines: LineItem[]): string | null {
  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index];
    // a line may have no name: it is called by its position
    const label = line.name.trim() || `Item ${index + 1}`;
    if (parseNonNegative(line.quantity) === null) return `${label}: enter a quantity (0 or more).`;
    if (line.unitPrice.trim() !== '' && parseNonNegative(line.unitPrice) === null) return `${label}: the price is not a valid number.`;
  }
  return null;
}

/** The lines as the API takes them (`lines` of a purchase order). A blank price is saved as 0; the server writes the cost code's label itself. */
export function toLinePayload(lines: LineItem[]): Array<Record<string, unknown>> {
  return lines.map((line) => ({
    // a line already saved is sent with its id: the server keeps it (bills point at it); a new one has none
    id: UUID.test(line.key) ? line.key : undefined,
    catalog_item_id: line.catalogItemId,
    vendor_id: line.vendorId,
    item_name: line.name.trim(),
    cost_code_id: line.costCodeId || null,
    description: line.description.trim() || null,
    unit: line.unit.trim() || null,
    quantity: parseNonNegative(line.quantity) ?? 0,
    unit_price: parseNonNegative(line.unitPrice) ?? 0,
    price_source: line.priceSource,
  }));
}

/** A saved line (purchase_order_item row) as a table line. A linked one is linked to what it says now. */
export function lineFromRow(row: Record<string, any>, orderVendorId?: string | null): LineItem {
  const line: LineItem = {
    key: row.id,
    catalogItemId: row.catalog_item_id ?? null,
    name: row.item_name ?? '',
    costCodeId: row.cost_code_id ?? '',
    description: row.description ?? '',
    unit: row.unit ?? '',
    quantity: String(Number(row.quantity ?? 1)),
    unitPrice: row.unit_price === null || row.unit_price === undefined ? '0' : String(Number(row.unit_price)),
    priceSource: row.price_source ?? null,
    vendorId: row.vendor_id ?? null,
    // a saved line whose vendor is not the order's was picked on purpose: it is not overridden when the order's vendor changes
    vendorChoice: row.vendor_id && orderVendorId && row.vendor_id !== orderVendorId ? 'manual' : 'auto',
    linkBaseline: null,
    unlinkedNotice: false,
    billAmount: '',
    sourceLineId: null,
  };
  return row.catalog_item_id ? { ...line, linkBaseline: baselineOf(line) } : line;
}

/** The vendor every item names, when it is the same one on all of them; otherwise none (each item keeps its own). Used by Create PO from a change order / selection. */
export function commonVendorOf(lines: Array<Pick<LineItem, 'vendorId'>>): string | undefined {
  const first = lines[0]?.vendorId;
  return first && lines.every((line) => line.vendorId === first) ? first : undefined;
}
